import { ReactNode, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import Login from "@/pages/login";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <Login onLoginSuccess={() => {
      // Authentication state will update automatically via useAuth hook
      window.location.reload(); // Simple way to refresh the app state
    }} />;
  }

  // Check if user has CMS access (AthlynAdmin or AthlynTeam)
  if (user && !user.roles.some(role => ['AthlynAdmin', 'AthlynTeam'].includes(role))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-md">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">
            You do not have permission to access the Athlyn CMS Portal. 
            This portal is restricted to Athlyn administrators and team members only.
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Logged in as: <strong>{user.firstName} {user.lastName}</strong><br />
            Role(s): {user.roles.join(', ')}
          </p>
          <button 
            onClick={() => {
              // Clear auth and reload
              sessionStorage.clear();
              window.location.reload();
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // User is authenticated and has CMS access
  return <>{children}</>;
}